import React from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';
import { ConnectionPool } from '@gitbook/util-websocket';

import {
    GitBookAgentClient,
    type GitBookAgentClientOptions,
    type GitBookAgentClientState,
} from './GitBookAgentClient';

type AgentPool = ConnectionPool<GitBookAgentClient>;

const AgentContext = React.createContext<AgentPool | null>(null);

export function AgentProvider({ children }: { children: React.ReactNode }) {
    const pool = React.useMemo(
        () =>
            new ConnectionPool<GitBookAgentClient>({
                onDestroy: (agent) => {
                    agent.disconnect();
                },
                ttl: 1000 * 60 * 5,
            }),
        []
    );

    return <AgentContext.Provider value={pool}>{children}</AgentContext.Provider>;
}

/**
 * Hook to get a new agent client.
 */
export function useCreateAgent() {
    const pool = React.useContext(AgentContext);
    if (!pool) {
        throw new Error('useCreateAgent should be used within an AgentProvider');
    }

    return React.useCallback(
        (options: GitBookAgentClientOptions) => {
            return pool.get(options.pathname, () => {
                const newAgent = new GitBookAgentClient(options);
                newAgent.connect();

                return newAgent;
            });
        },
        [pool]
    );
}

/**
 * Hook to connect to a content agent.
 */
export function useAgent(options: GitBookAgentClientOptions) {
    const pool = React.useContext(AgentContext);
    if (!pool) {
        throw new Error('useAgent should be used within an AgentProvider');
    }

    const getToken = useEventCallback(options.getToken);

    const agent = React.useMemo(() => {
        return pool.get(options.pathname, () => {
            const newAgent = new GitBookAgentClient({
                getToken,
                pathname: options.pathname,
            });
            newAgent.connect();

            return newAgent;
        });
    }, [pool, options.pathname, getToken]);

    React.useEffect(() => {
        return pool.keepAlive(agent);
    }, [pool, agent]);

    return agent;
}

/**
 * Hook to get the state of the agent.
 */
export function useAgentState<
    MapState extends undefined | ((state: GitBookAgentClientState) => any) = undefined,
>(
    agent: GitBookAgentClient,
    mapState?: MapState
): MapState extends (state: GitBookAgentClientState) => any
    ? ReturnType<MapState>
    : GitBookAgentClientState {
    return React.useSyncExternalStore(
        React.useCallback(
            (callback) => {
                return agent.listen('state', callback);
            },
            [agent]
        ),
        React.useCallback(() => {
            return mapState ? mapState(agent.state) : agent.state;
        }, [agent, mapState])
    );
}
